// ItemForm.jsx
import React, { useState, useRef, useEffect, useCallback } from "react";
import styled from "styled-components";
import TextField from "../../common/TextField";
import ImageUploader from "../../common/ImageUploader";
import ItemHelp from "./AddItem/ItemHelp";
import QRCode from "../../common/QRCode";
import IconButton from "../../common/Button";
import ChipStatus from "../../common/ChipStatus";
import Checkbox from "../../common/Checkbox";

const ItemForm = React.forwardRef(
  (
    {
      initialValues,
      sections,
      initialSectionAnswers, // 3️⃣ no default {}
      initialSectionPrompts,
      onSubmit,
      onCancel,
      formError,
    },
    ref
  ) => {
    // Initialize once from stable props
    const [itemData, setItemData] = useState(() => ({
      ...initialValues,
      section_answers: initialSectionAnswers,
      section_prompts: initialSectionPrompts,
    }));
    const [imageChanged, setImageChanged] = useState(false);
    const formRef = useRef();

    React.useImperativeHandle(ref, () => ({
      requestSubmit: () => formRef.current.requestSubmit(),
    }));

    // Reset only if initialValues object actually changes
    useEffect(() => {
      setItemData({
        ...initialValues,
        section_answers: initialSectionAnswers,
        section_prompts: initialSectionPrompts,
      });
    }, [initialValues, initialSectionAnswers, initialSectionPrompts]);

    const handleInputChange = (field, value) => {
      setItemData((prev) => ({ ...prev, [field]: value }));
    };

    // 4️⃣ Memoize so identity stays the same
    const handleHelpUpdate = useCallback(
      ({ section_prompts, section_answers }) => {
        setItemData((prev) => ({
          ...prev,
          section_answers,
          section_prompts,
        }));
      },
      []
    );

    const handleImageUpdate = ({ file, preview, altText }) => {
      setItemData((prev) => ({
        ...prev,
        imageFile: file,
        image_url: preview,
        image_alt_text: altText,
      }));
      setImageChanged(true);
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      if (!itemData.title || !itemData.description) return;

      // strip out empty answers
      const cleaned = Object.fromEntries(
        Object.entries(itemData.section_answers || {})
          .map(([sid, arr]) => [sid, arr.filter((a) => a.trim())])
          .filter(([_, arr]) => arr.length)
      );

      const overrides = Object.entries(itemData.section_prompts || {})
        .filter(([, p]) => p.trim())
        .map(([section, prompt]) => ({ section, prompt }));

      onSubmit({
        ...itemData,
        section_answers: cleaned,
        prompt_overrides: overrides,
        imageChanged,
      });
    };

    return (
      <FormContainer
        ref={formRef}
        onSubmit={handleSubmit}
        encType="multipart/form-data"
      >
        {/* Title & Desc */}
        <FormSection>
          <FormTitle>Item name</FormTitle>
          <InputContainer>
            <label>
              Title <span className="error">*</span>
            </label>
            <TextField
              placeholder="Enter item title"
              maxLength={100}
              name="title"
              value={itemData.title}
              onChange={(e) => handleInputChange("title", e.target.value)}
              required
            />
          </InputContainer>
          <InputContainer>
            <label>
              Description <span className="error">*</span>
            </label>
            <TextField
              placeholder="Enter item description"
              maxLength={250}
              isTextArea
              name="description"
              value={itemData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              required
            />
          </InputContainer>
        </FormSection>

        {/* Image */}
        <FormSection>
          <FormTitle>Item image</FormTitle>
          <ImageUploader
            onDataUpdate={handleImageUpdate}
            imageFromParent={itemData.image_url}
            altTextFromParent={itemData.image_alt_text}
          />
        </FormSection>

        {/* Help (prompt + per‐section answers) */}
        <FormSection>
          <FormTitle>Item help</FormTitle>
          <ItemHelp
            sections={sections}
            initialSectionPrompts={itemData.section_prompts}
            initialSectionAnswers={itemData.section_answers}
            onDataUpdate={handleHelpUpdate}
          />
        </FormSection>

        {/* Resource link */}
        <FormSection>
          <FormTitle>Resource link</FormTitle>
          <InputContainer>
            <label>URL</label>
            <TextField
              placeholder="Enter web link here"
              maxLength={200}
              value={itemData.resource_link}
              onChange={(e) =>
                handleInputChange("resource_link", e.target.value)
              }
            />
            <QRCode resourceLink={itemData.resource_link} />
          </InputContainer>
        </FormSection>

        {/* Hide flag */}
        <FormSection>
          <FormTitle>Hide from service-user list</FormTitle>
          <Checkbox
            label="Hide this item when creating a care plan"
            checked={itemData.hide_from_service_user_specific}
            onChange={(e) =>
              handleInputChange(
                "hide_from_service_user_specific",
                e.target.checked
              )
            }
          />
        </FormSection>

        {formError && <ChipStatus status="danger">{formError}</ChipStatus>}

        <HrLine />

        <ButtonContainer>
          <IconButton variant="secondary" onClick={onCancel}>
            Cancel
          </IconButton>
          <IconButton type="submit" variant="primary">
            Save
          </IconButton>
        </ButtonContainer>
      </FormContainer>
    );
  }
);

export default ItemForm;

// Styled components
const FormContainer = styled.form`
  width: 60%;
  margin: 0 auto;
`;
const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
  margin-bottom: 24px;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  background: #fff;
`;
const FormTitle = styled.h5``;
const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
`;
const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
`;
const HrLine = styled.div`
  height: 1px;
  background: repeating-linear-gradient(
    to right,
    #ccc,
    #ccc 10px,
    transparent 5px,
    transparent 25px
  );
`;
